import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../app/store';
import { SessionStateInterface } from '../models/session';
import _ from 'lodash';

const initialState: SessionStateInterface = {
  sessionId: '',
  matchingPnrs: [],
  segments: [],
  device: 'DESKTOP',
  testMode: [],
  adokMessages: [],
  routeMessages: [],
  adokFlights: [],
  passengerClasses: [],
  checkedInFltPax: undefined,
  checkedInPax: undefined,
  notValidFltPax: undefined,
  checkinFltPax: undefined,
  checkinPax: undefined,
  infantPax: [],
  onwardDisabled: false,
  addedFoidNumbers: [],
  locale: 'en',
  selectedCorNatSelection: {},
  encryptedDeeplink: '',
  selectFlight: [],
  regulatoryBehaviour: '',
  regulatoryMessages: [],
  apisSecurity: [],
  apisAdcConditional: false,
  apisAdcPreConditional: false,
  apisAdcInfo: false,
  apisAdcNok: false,
  getCheckinDetailsInput: [],
  apisResponsePassengers: [],
  tciPax: [],
  profileData: [],
  safeTravelSkipped: false,
  backendWs: undefined,
  appId: '',
  storedModel: {},
  apisPassengersDocoMissing: undefined,
  identityDocumentData: undefined,
  validationResult: [],
};

interface WritableDraft {
  [key: string]: any;
}

export const sessionSlice = createSlice({
  name: 'session',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    resetSession: (state) => {
      return initialState;
    },
    setSessionId: (state, action: PayloadAction<any>) => {
      state.sessionId = action.payload;
    },
    setMatchingPnrs: (state, action: PayloadAction<any>) => {
      state.matchingPnrs = action.payload;
    },
    setSegments: (state, action: PayloadAction<any>) => {
      state.segments = action.payload;
    },
    setDevice: (state, action: PayloadAction<string>) => {
      state.device = action.payload;
    },
    setTestMode: (state, action: PayloadAction<string>) => {
      state.testMode = action.payload;
    },
    setAdokMessages: (state, action: PayloadAction<any>) => {
      state.adokMessages = action.payload;
    },
    setRouteMessages: (state, action: PayloadAction<any>) => {
      state.routeMessages = action.payload;
    },
    setAdokFlights: (state, action: PayloadAction<any>) => {
      state.adokFlights = action.payload;
    },
    setPassengerClasses: (state, action: PayloadAction<any>) => {
      state.passengerClasses = action.payload;
    },
    setCheckedInFltPax: (state, action: PayloadAction<any>) => {
      state.checkedInFltPax = action.payload;
    },
    setCheckedInPax: (state, action: PayloadAction<any>) => {
      state.checkedInPax = action.payload;
    },
    setNotValidFltPax: (state, action: PayloadAction<any>) => {
      state.notValidFltPax = action.payload;
    },
    setCheckinFltPax: (state, action: PayloadAction<any>) => {
      state.checkinFltPax = action.payload;
    },
    setCheckinPax: (state, action: PayloadAction<any>) => {
      state.checkinPax = action.payload;
    },
    setInfantPax: (state, action: PayloadAction<any>) => {
      state.infantPax = action.payload;
    },
    setOnwardDisabled: (state, action: PayloadAction<any>) => {
      state.onwardDisabled = action.payload;
    },
    setAddedFoidNumbers: (state, action: PayloadAction<any>) => {
      state.addedFoidNumbers = action.payload;
    },
    setLocale: (state, action: PayloadAction<any>) => {
      state.locale = action.payload;
    },
    setCorNatSelection: (state, action: PayloadAction<any>) => {
      state.selectedCorNatSelection = action.payload;
    },
    setEncryptedDeeplink: (state, action: PayloadAction<any>) => {
      state.encryptedDeeplink = action.payload;
    },
    setSelectFlight: (state, action: PayloadAction<any>) => {
      state.selectFlight = action.payload;
    },
    setRegulatoryBehaviour: (state, action: PayloadAction<any>) => {
      state.regulatoryBehaviour = action.payload;
    },
    setRegulatoryMessages: (state, action: PayloadAction<any>) => {
      state.regulatoryMessages = action.payload;
    },
    setApisSecurity: (state, action: PayloadAction<any>) => {
      state.apisSecurity = action.payload;
    },
    setApisAdcConditional: (state, action: PayloadAction<any>) => {
      state.apisAdcConditional = action.payload;
    },
    setApisAdcPreConditional: (state, action: PayloadAction<any>) => {
      state.apisAdcPreConditional = action.payload;
    },
    setApisAdcInfo: (state, action: PayloadAction<any>) => {
      state.apisAdcInfo = action.payload;
    },
    setApisAdcNok: (state, action: PayloadAction<any>) => {
      state.apisAdcNok = action.payload;
    },
    setGetCheckinDetailsInput: (state, action: PayloadAction<any>) => {
      state.getCheckinDetailsInput = action.payload;
    },
    setApisResponsePassengers: (state, action: PayloadAction<any>) => {
      state.apisResponsePassengers = action.payload;
    },
    setTciPax: (state, action: PayloadAction<any>) => {
      state.tciPax = action.payload;
    },
    setBackendWs: (state, action: PayloadAction<any>) => {
      state.backendWs = action.payload;
    },
    setAppId: (state, action: PayloadAction<any>) => {
      state.appId = action.payload;
    },
    setApisPassengersDocoMissing: (state, action: PayloadAction<any>) => {
      state.apisPassengersDocoMissing = action.payload;
    },
    setProfileData: (
      state: WritableDraft,
      action: PayloadAction<{ key: string; path?: string | null; value: any }>
    ) => {
      const { key, value, path } = action.payload;
      if (path) {
        let element = state[key] || null;
        if (element) {
          _.set(element, path, value);
        }
      }
    },
    addStoredModel: (state, action: PayloadAction<{ key: string; value: any }>) => {
      let key = action.payload.key;
      let value = action.payload.value;
      state.storedModel[key] = value;
    },
    setIdentityDocumentData: (state, action) => {
      state.identityDocumentData = action.payload;
    },
    setApisRequired: (state, action) => {
      state.apisRequired = action.payload;
    }
  },
});

export const {
  resetSession,
  setMatchingPnrs,
  setDevice,
  setTestMode,
  setAdokMessages,
  setRouteMessages,
  setAdokFlights,
  setPassengerClasses,
  setCheckedInFltPax,
  setCheckedInPax,
  setNotValidFltPax,
  setCheckinFltPax,
  setInfantPax,
  setOnwardDisabled,
  setAddedFoidNumbers,
  setCheckinPax,
  setLocale,
  setSessionId,
  setCorNatSelection,
  setSelectFlight,
  setRegulatoryBehaviour,
  setApisSecurity,
  setRegulatoryMessages,
  setApisAdcConditional,
  setApisAdcPreConditional,
  setApisAdcInfo,
  setApisAdcNok,
  setGetCheckinDetailsInput,
  setApisResponsePassengers,
  setTciPax,
  setProfileData,
  setBackendWs,
  addStoredModel,
  setApisPassengersDocoMissing,
  setIdentityDocumentData,
  setSegments,
  setApisRequired,
} = sessionSlice.actions;

export const selectSessionModel = (state: RootState) => state.session;
export const selectSessionId = (state: RootState) => state.session.sessionId;

export default sessionSlice.reducer;
